/**
 * What's new (People Part 6): the changelog's "For people" voice, newest first, dated — written
 * for chairs, not for the build. Opening it puts the bell's dot out for this version.
 *
 * James, Oct 14: the head says which build is running and when it went up — the same answer
 * the status bar reads — and there is one entry for each time the box was updated, named by
 * its hash. Every entry is whole, and its **bold** and `code` are drawn, never printed raw. The
 * entry not yet named for a push is the build that is running, when no other entry names it.
 *
 * James, Oct 27: entries are rules, not help — one per feature, each WHERE · WHAT YOU CAN DO NOW ·
 * TRY IT, from `docs/WHATS-NEW.md`; what has no screen is a line under Behind the scenes, after
 * them. An update older than the people's file keeps its changelog sentences.
 */
import { Fragment, useEffect, type ReactNode } from 'react';
import { Card, CardRow, PageBody, PageHeader, formatDate, formatDateTime } from '@cider/ui';
import { api } from './api.js';
import { useLoad } from '../views/money/shared.js';

interface Entry {
  title: string;
  where: string;
  what: string;
  tryIt: string;
}
interface Release {
  version: string;
  date: string | null;
  entries: Entry[];
  behind: string[];
  forPeople: string[];
}
interface Answer {
  version: string;
  running?: { version: string; commit: string; deployedAt: string };
  releases: Release[];
}

/** **bold** and `code`, drawn; everything else as written. */
export function inline(text: string): ReactNode {
  return text
    .split(/(\*\*[^*]+\*\*|`[^`]+`)/g)
    .map((part, i) =>
      part.startsWith('**') && part.endsWith('**') ? (
        <strong key={i}>{part.slice(2, -2)}</strong>
      ) : part.startsWith('`') && part.endsWith('`') ? (
        <code key={i}>{part.slice(1, -1)}</code>
      ) : (
        <Fragment key={i}>{part}</Fragment>
      ),
    );
}

/** "2.0.0-alpha.72+8552cb8" → "8552cb8"; "2.0.0-alpha.72" → null. */
const hashOf = (version: string): string | null => /\+([0-9a-f]{7,})$/.exec(version)?.[1] ?? null;

export function WhatsNew({ onSeen }: { onSeen: (version: string) => void }): ReactNode {
  const w = useLoad<Answer>('/whats-new');
  const version = w.data?.version ?? null;
  const running = w.data?.running ?? null;
  useEffect(() => {
    if (!version) return;
    api('POST', '/auth/seen-version', { version })
      .then(() => {
        onSeen(version);
      })
      .catch(() => undefined);
  }, [version, onSeen]);
  const releases = w.data?.releases ?? [];
  const here = (r: Release): boolean => {
    const h = hashOf(r.version);
    return !!running && !!h && running.commit.startsWith(h);
  };
  const named = releases.some(here);
  const head = (r: Release): string => {
    // The entry still waiting for its push is the running build when nothing else names it.
    if (r.version.endsWith('+waiting'))
      return running && !named
        ? `${running.version} · ${formatDateTime(running.deployedAt)} · YOU ARE HERE`
        : 'Waiting for the next update';
    const date = r.date ? ` · ${formatDate(`${r.date}T12:00:00Z`, { year: true })}` : '';
    return `${r.version}${date}${here(r) ? ' · YOU ARE HERE' : ''}`;
  };
  return (
    <>
      <PageHeader
        eyebrow="CIDER"
        title="What’s new"
        lede={
          running
            ? `Running ${running.version}, updated ${formatDateTime(running.deployedAt)}. Each update, newest first: what is new, where it is, and how to try it.`
            : 'Each update, newest first: what is new, where it is, and how to try it.'
        }
      />
      <PageBody>
        {w.error ? (
          <p className="ui-form__refusal" role="alert">
            {w.error}
          </p>
        ) : null}
        {releases.map((r) => (
          <Fragment key={r.version}>
            <Card head={head(r)}>
              {r.entries.length > 0 ? (
                r.entries.map((e) => (
                  <CardRow
                    key={e.title}
                    a={<strong>{e.title}</strong>}
                    c={
                      <>
                        <span className="whats-new__part">
                          <b>WHERE</b> {e.where}
                        </span>
                        <span className="whats-new__part">
                          <b>WHAT YOU CAN DO NOW</b> {e.what}
                        </span>
                        <span className="whats-new__part">
                          <b>TRY IT</b> {e.tryIt}
                        </span>
                      </>
                    }
                  />
                ))
              ) : r.forPeople.length > 0 ? (
                r.forPeople.map((line, i) => <CardRow key={i} a={inline(line)} />)
              ) : r.behind.length === 0 ? (
                <CardRow a="Nothing written for people in this update." />
              ) : null}
            </Card>
            {r.behind.length > 0 ? (
              <Card head="Behind the scenes">
                {r.behind.map((line, i) => (
                  <CardRow key={i} a={inline(line)} />
                ))}
              </Card>
            ) : null}
          </Fragment>
        ))}
      </PageBody>
    </>
  );
}
